/**
 * Replay Entraînement à partir du détail d'une partie humaine (stats différé).
 */

import { BACKEND_URL } from '../config';
import { Contract } from '../types/game';

const SEAT_LABELS = ['Sud', 'Est', 'Nord', 'Ouest'] as const;

export interface DiffereStatsDealTrick {
  trickIndex: number;
  leader: number;
  winner: number;
  cards: number[];
  players: number[];
}

export interface TrickScoreAlternative {
  card: number;
  scoreDelta: number;
  bestScore: number;
  bestPseudo: string;
  /** Pseudos ayant le même bestScore pour cette alternative (sinon [bestPseudo]). */
  bestPseudos?: string[];
  bestResultId: number;
  kind: 'equivalent' | 'bifurcation' | 'delayed_bifurcation' | 'pre_bifurcation_variant';
  /** 1 = règle smart « 1ère carte humaine » ; 2 = redondante vs bif. plus tardive (test). */
  preVariantType?: 1 | 2;
  trickIndex: number;
  effectTrickIndex?: number;
  /** Cartes humaines distinctes des contributeurs (même bif), index = trickIndex. */
  peerCardsByTrick?: number[][];
}

export interface DiffereStatsDealDetail {
  differeId: number;
  resultId: number;
  dealIndex: number;
  pseudo: string;
  pseudoDisplay: string | null;
  perDealRank: number | null;
  perDealRankExAequo?: boolean;
  dealScore: number | null;
  contract: number;
  contractLabel: string;
  takerPosition: number;
  dealer: number;
  isAttackDeal: boolean;
  humanSeat: number;
  tricks: DiffereStatsDealTrick[];
  initialHands: Record<string, number[]>;
  chien: number[];
  ecart: number[];
  prefixMatchPctByTrick?: number[];
  prefixMatchSampleSize?: number;
  betterScoreAlternativesByTrick?: TrickScoreAlternative[][];
}

export function formatPerDealRankLabel(rank: number, exAequo?: boolean): string {
  return exAequo ? `${rank} ex` : String(rank);
}

export type EquivMode = 'off' | 'normal' | 'max' | 'maxplus';

export interface FetchDiffereStatsDealDetailParams {
  sessionPseudo: string;
  differeId: number;
  resultId: number;
  dealIndex: number;
  topX: number;
  contractTypes: string;
  /** Mode d'équivalence pour les bifurcations (max masque les reports Type A/B). */
  equivMode: EquivMode;
  /** @deprecated préférer equivMode ; true → normal, false → off */
  groupEquiv?: boolean;
  lowRankLimit: number;
  /** Ajustements smart delayed bif (défaut true). Passer false pour la logique brute. */
  smartBif?: boolean;
}

export async function fetchDiffereStatsDealDetail(
  params: FetchDiffereStatsDealDetailParams,
): Promise<DiffereStatsDealDetail> {
  const equivMode = params.equivMode
    ?? (params.groupEquiv === true ? 'normal' : 'off');
  const q = new URLSearchParams({
    resultId: String(params.resultId),
    dealIndex: String(params.dealIndex),
    topX: String(params.topX),
    contractTypes: params.contractTypes,
    equivMode,
  });
  if (equivMode !== 'off') {
    q.set('groupEquiv', 'true');
    q.set('lowRankLimit', String(params.lowRankLimit));
  }
  q.set('smartBif', params.smartBif === false ? '0' : '1');
  const res = await fetch(
    `${BACKEND_URL}/api/admin/stats/differe/${params.differeId}/party-detail?${q}&pseudo=${encodeURIComponent(params.sessionPseudo)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Détail impossible');
  return data as DiffereStatsDealDetail;
}

/** Cartes humaines (Sud) jouées pli par pli sur une donne stats. */
export function humanCardsPlayedOnDeal(detail: DiffereStatsDealDetail): number[] {
  return [...detail.tricks]
    .sort((a, b) => a.trickIndex - b.trickIndex)
    .map(tr => {
      const i = tr.players.indexOf(detail.humanSeat);
      return i >= 0 ? tr.cards[i]! : null;
    })
    .filter((c): c is number => c != null);
}

function trickCardsBySeat(tr: DiffereStatsDealTrick): Map<number, number> {
  const bySeat = new Map<number, number>();
  for (let i = 0; i < tr.cards.length; i++) {
    const seat = tr.players[i];
    if (seat == null || seat < 0) continue;
    bySeat.set(seat, tr.cards[i]!);
  }
  return bySeat;
}

/** Clés `${trickIndex}:${seat}` des cartes différentes entre deux replays de la même donne. */
export function computeDealTrickSeatDiffKeys(
  baseline: Pick<DiffereStatsDealDetail, 'tricks'>,
  current: Pick<DiffereStatsDealDetail, 'tricks'>,
  opts?: {
    /** Ne surligner qu'à partir de ce pli (inclus). */
    fromTrickIndex?: number;
    /** Au pli fromTrickIndex, ne surligner que ce siège ; plis suivants : tous les sièges. */
    humanSeatOnlyAtFromTrick?: number;
  },
): Set<string> {
  const baselineByTrick = new Map(baseline.tricks.map(t => [t.trickIndex, t]));
  const keys = new Set<string>();

  for (const tr of current.tricks) {
    if (opts?.fromTrickIndex != null && tr.trickIndex < opts.fromTrickIndex) continue;

    const prev = baselineByTrick.get(tr.trickIndex);
    if (!prev) {
      if (opts?.fromTrickIndex != null && tr.trickIndex === opts.fromTrickIndex
        && opts.humanSeatOnlyAtFromTrick != null) {
        keys.add(`${tr.trickIndex}:${opts.humanSeatOnlyAtFromTrick}`);
      } else {
        for (let i = 0; i < tr.cards.length; i++) {
          const seat = tr.players[i];
          if (seat != null && seat >= 0) keys.add(`${tr.trickIndex}:${seat}`);
        }
      }
      continue;
    }
    const prevBySeat = trickCardsBySeat(prev);
    const curBySeat = trickCardsBySeat(tr);
    const seats = new Set([...prevBySeat.keys(), ...curBySeat.keys()]);

    if (opts?.fromTrickIndex != null && tr.trickIndex === opts.fromTrickIndex
      && opts.humanSeatOnlyAtFromTrick != null) {
      const seat = opts.humanSeatOnlyAtFromTrick;
      if (prevBySeat.get(seat) !== curBySeat.get(seat)) {
        keys.add(`${tr.trickIndex}:${seat}`);
      }
      continue;
    }

    for (const seat of seats) {
      if (prevBySeat.get(seat) !== curBySeat.get(seat)) {
        keys.add(`${tr.trickIndex}:${seat}`);
      }
    }
  }

  return keys;
}

export function diffHighlightOptionsForAlternative(
  alt: TrickScoreAlternative,
  humanSeat: number,
): {
  fromTrickIndex?: number;
  humanSeatOnlyAtFromTrick?: number;
} | undefined {
  if (alt.kind === 'bifurcation') {
    return { fromTrickIndex: alt.trickIndex, humanSeatOnlyAtFromTrick: humanSeat };
  }
  if (alt.kind === 'delayed_bifurcation' || alt.kind === 'pre_bifurcation_variant') {
    return { fromTrickIndex: alt.effectTrickIndex ?? alt.trickIndex };
  }
  return undefined;
}

function syntheticBids(dealer: number, taker: number, contract: number, playerCount = 4) {
  const bids: Array<{ player: number; bid: number; timestamp: number }> = [];
  for (let i = 0; i < playerCount; i++) {
    const p = (dealer + 1 + i) % playerCount;
    bids.push({ player: p, bid: p === taker ? contract : Contract.Pass, timestamp: 0 });
  }
  return bids;
}

export function buildTrainingGameDataFromStatsDetail(detail: DiffereStatsDealDetail) {
  const pc = 4;
  const dealer = detail.dealer;
  const taker = detail.takerPosition;
  const contract = detail.contract;
  const humanLabel = detail.pseudoDisplay || detail.pseudo;

  const players = Array.from({ length: pc }, (_, position) => ({
    position,
    displayName: position === 0 ? humanLabel : `Bot (${SEAT_LABELS[position]})`,
    isHuman: position === 0,
    isBot: position !== 0,
  }));

  const initialHands: Record<number, number[]> = {};
  for (const [k, v] of Object.entries(detail.initialHands ?? {})) {
    const idx = Number(k);
    if (Number.isFinite(idx) && idx >= 0 && idx < pc) {
      initialHands[idx] = [...v];
    }
  }

  const tricks = (detail.tricks ?? []).map(t => ({
    trickNumber: t.trickIndex + 1,
    leader: t.leader,
    winner: t.winner,
    cards: t.cards.map((card, i) => ({
      player: t.players[i] ?? ((t.leader + i) % pc),
      card,
      timestamp: 0,
    })),
  }));

  return {
    players,
    playerCount: pc,
    dealer,
    taker,
    contract,
    initialHands,
    chien: [...(detail.chien ?? [])],
    ecart: [...(detail.ecart ?? [])],
    bids: syntheticBids(dealer, taker, contract, pc),
    tricks,
    poignees: {},
  };
}

export function buildReplaySeatLabels(detail: DiffereStatsDealDetail): string[] {
  const humanLabel = detail.pseudoDisplay || detail.pseudo;
  return SEAT_LABELS.map((seat, pos) =>
    pos === 0 ? `${humanLabel} (${seat})` : `Bot (${seat})`,
  );
}
